import type { DataRow, TableGetRows } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import type { AppointmentPlanningPreviewRow } from "../../../../../../../api/kehrblatt/planning"
import type { CancelReplanTarget } from "../../../../../../../api/kehrblatt/schedule-events"
import { isActiveContactOrder } from "../../../../../../../_shared/_logic/outreach-presentation"
import type { CancelReplanRow, PlannedRow } from "../cancel-replan-contract"
import type { WorkOrderRow } from "../work-order-table-model"

export const createStaticRowGetter =
  (source: unknown[]): TableGetRows =>
  ({ limit, offset }) => {
    const start = offset ?? 0
    const end = start + (limit && limit > 0 ? limit : 100)

    return { rows: source.slice(start, end) as DataRow[], total: source.length }
  }

export const createStaticRowsRefreshKey = (source: unknown[]): string =>
  source
    .map((row, index) => {
      const item = row as {
        id?: unknown
        removed?: unknown
        event?: { id?: unknown }
        preview?: { status?: unknown; appointment?: { start_at?: unknown } }
        activeContactOrder?: { id?: unknown; status?: unknown }
      }

      return [
        item.id ?? index,
        item.removed ?? "",
        item.event?.id ?? "",
        item.preview?.status ?? "",
        item.preview?.appointment?.start_at ?? "",
        item.activeContactOrder?.id ?? "",
        item.activeContactOrder?.status ?? "",
      ].join(":")
    })
    .join("|")

export const buildCancelReplanRow = ({
  event,
  workOrderById,
  workOrderRowById,
  propertyById,
}: {
  event: ScheduleEvent
  workOrderById: Map<string, WorkOrder>
  workOrderRowById?: Map<string, WorkOrderRow>
  propertyById: Map<string, Property>
}): CancelReplanRow => {
  const workOrder = event.work_order_id ? (workOrderById.get(event.work_order_id) ?? null) : null
  const workOrderRow = workOrder?.id ? (workOrderRowById?.get(workOrder.id) ?? null) : null
  const property =
    workOrder?.property_id || event.property_id
      ? (propertyById.get(String(workOrder?.property_id || event.property_id)) ?? null)
      : null

  return { id: event.id, event, workOrder, workOrderRow, property, removed: false }
}

export const buildCancelReplanRows = ({
  events,
  workOrderById,
  workOrderRowById,
  propertyById,
}: {
  events: ScheduleEvent[]
  workOrderById: Map<string, WorkOrder>
  workOrderRowById?: Map<string, WorkOrderRow>
  propertyById: Map<string, Property>
}): CancelReplanRow[] =>
  events.map((event) =>
    buildCancelReplanRow({
      event,
      workOrderById,
      workOrderRowById,
      propertyById,
    }),
  )

export const buildWorkOrderScheduleBatchRows = ({
  workOrders,
  eventByWorkOrderId = {},
  workOrderRowById,
  propertyById,
  activeContactOrderByWorkOrderId = {},
}: {
  workOrders: WorkOrder[]
  eventByWorkOrderId?: Record<string, ScheduleEvent | null>
  workOrderRowById?: Map<string, WorkOrderRow>
  propertyById: Map<string, Property>
  activeContactOrderByWorkOrderId?: Record<string, ContactOrder | null>
}): CancelReplanRow[] =>
  workOrders.map((workOrder) => {
    const event = eventByWorkOrderId[workOrder.id] ?? null
    const property = workOrder.property_id
      ? (propertyById.get(String(workOrder.property_id)) ?? null)
      : null
    const workOrderRow = workOrderRowById?.get(workOrder.id) ?? null

    return {
      id: event?.id ?? workOrder.id,
      event,
      workOrder,
      workOrderRow,
      property,
      activeContactOrder: activeContactOrderByWorkOrderId[workOrder.id] ?? null,
      removed: false,
    }
  })

export const applyCancelReplanPreviewTargets = ({
  rows,
  targets,
  eventById,
}: {
  rows: CancelReplanRow[]
  targets: CancelReplanTarget[]
  eventById: Map<string, ScheduleEvent>
}): CancelReplanRow[] =>
  rows.map((row) => {
    const target = targets.find((item) => item.work_order_id === row.workOrder?.id)
    const event = target?.event_id ? (eventById.get(target.event_id) ?? row.event) : row.event

    return {
      ...row,
      event,
      id: event?.id ?? row.id,
      removed: target?.status === "skipped",
    }
  })

export const setCancelReplanRowRemoved = (
  rows: CancelReplanRow[],
  rowId: string,
  removed: boolean,
): CancelReplanRow[] => rows.map((row) => (row.id === rowId ? { ...row, removed } : row))

export const markRowsWithoutCanceledEventRemoved = (
  rows: CancelReplanRow[],
  canceledIds: Set<string>,
): CancelReplanRow[] =>
  rows.map((row) =>
    row.event?.id && canceledIds.has(row.event.id) ? row : { ...row, removed: true },
  )

export const buildPlannedRowsFromPreview = ({
  rows,
  previewRows,
}: {
  rows: CancelReplanRow[]
  previewRows: AppointmentPlanningPreviewRow[]
}): PlannedRow[] =>
  rows.map((row) => ({
    id: row.id,
    row,
    preview: previewRows.find((entry) => entry.client_row_id === row.workOrder?.id) ?? null,
    event: null,
    previousEvent: row.event,
  }))

export const buildPlanningFallbackRows = (rows: CancelReplanRow[]): PlannedRow[] =>
  rows.map((row) => ({ id: row.id, row, preview: null, event: null, previousEvent: row.event }))

export const buildPlannedRowsFromCommit = ({
  rows,
  scheduledEvents,
  currentPlannedRows,
}: {
  rows: CancelReplanRow[]
  scheduledEvents: ScheduleEvent[]
  currentPlannedRows: PlannedRow[]
}): PlannedRow[] =>
  rows.map((row) => {
    const currentRow = currentPlannedRows.find((entry) => entry.row.id === row.id)

    return {
      id: row.id,
      row,
      preview: currentRow?.preview ?? null,
      event: scheduledEvents.find((event) => event.work_order_id === row.workOrder?.id) ?? null,
      previousEvent: currentRow?.previousEvent ?? row.event,
    }
  })

export const buildActiveContactOrderByWorkOrderId = (
  contactOrders: ContactOrder[],
): Record<string, ContactOrder> => {
  const result: Record<string, ContactOrder> = {}
  for (const order of contactOrders ?? []) {
    const refs =
      (order as { product_refs?: Record<string, unknown> | null }).product_refs &&
      typeof (order as { product_refs?: Record<string, unknown> | null }).product_refs === "object"
        ? (order as { product_refs?: Record<string, unknown> }).product_refs
        : null
    const workOrderId =
      order.work_order_id || (refs?.work_order_id ? String(refs.work_order_id) : "")

    if (!workOrderId || result[workOrderId] || !isActiveContactOrder(order)) continue

    result[workOrderId] = order
  }

  return result
}
